import type { ReaderContent } from '@/types/reader'
import type { LearningStatus } from './learning-state-core'

export type WordFamiliarityStatus =
  | 'new'
  | 'seen'
  | 'saved'
  | 'learning'
  | 'reviewed'
export type CharacterFamiliarityStatus = 'new' | 'seen'
export type GrammarFamiliarityStatus = 'new' | 'seen' | 'studying' | 'learned'
export type ContentFit = 'new' | 'stretch' | 'good-fit' | 'review'

export type StudyCardProgressLike = {
  state?: number | null
  reps?: number | null
  last_review?: string | null
}

export type ContentFamiliaritySummary = {
  contentType: 'story' | 'reader' | 'grammar'
  contentId: string
  totalWordCount: number
  seenWordCount: number
  savedWordCount: number
  studiedWordCount: number
  reviewedWordCount: number
  newWordCount: number
  seenWordRatio: number
  savedWordRatio: number
  studiedWordRatio: number
  reviewedWordRatio: number
  totalCharacterCount: number
  seenCharacterCount: number
  newCharacterCount: number
  characterCoverageRatio: number
  totalGrammarCount: number
  seenGrammarCount: number
  studiedGrammarCount: number
  learnedGrammarCount: number
  newGrammarCount: number
  grammarCoverageRatio: number
  progressRatio: number | null
  familiarityScore: number
  readinessScore: number
  fit: ContentFit
}

function uniqueNumbers(values: Array<number | null | undefined>) {
  return Array.from(
    new Set(
      values.filter((value): value is number => typeof value === 'number')
    )
  ).sort((a, b) => a - b)
}

function uniqueStrings(values: Array<string | null | undefined>) {
  return Array.from(
    new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
    )
  ).sort((a, b) => a.localeCompare(b))
}

export function ratio(count: number, total: number) {
  if (!Number.isFinite(count) || !Number.isFinite(total) || total <= 0) return 0
  return Math.min(1, Math.max(0, count / total))
}

export function extractReaderWordIds(content: ReaderContent): number[] {
  const tokenIds = content.blocks.flatMap((block) =>
    block.tokens.flatMap((token) => [
      token.entryId,
      ...(token.compositeIds ?? [])
    ])
  )
  const glossaryIds = Object.values(content.glossary).map((entry) => entry.id)
  return uniqueNumbers([...tokenIds, ...glossaryIds])
}

export function extractReaderCharacters(content: ReaderContent): string[] {
  const values = content.blocks.flatMap((block) => [
    block.originalText,
    ...block.tokens.flatMap((token) => [
      token.surface,
      token.simplified,
      token.traditional
    ])
  ])
  const text = values.filter(Boolean).join('')
  return uniqueStrings(text.match(/[\u3400-\u9fff\uf900-\ufaff]/g) ?? [])
}

export function extractReaderGrammarConceptIds(
  content: ReaderContent
): string[] {
  const summaryIds = (content.grammarSummary ?? []).map(
    (item) => item.conceptId
  )
  const usageIds = content.blocks.flatMap((block) =>
    (block.grammarUsages ?? []).map((usage) => usage.conceptId)
  )
  return uniqueStrings([...summaryIds, ...usageIds])
}

export function classifyWordFamiliarity(
  cards: StudyCardProgressLike[] = []
): WordFamiliarityStatus {
  if (!cards.length) return 'new'
  if (cards.some((card) => Boolean(card.last_review) || (card.reps ?? 0) > 0)) {
    return 'reviewed'
  }
  if (cards.some((card) => (card.state ?? 0) !== 0)) return 'learning'
  return 'saved'
}

export function learningStatusToWordFamiliarity(
  status: LearningStatus | null | undefined
): WordFamiliarityStatus {
  if (status === 'learned' || status === 'completed' || status === 'studying') {
    return 'learning'
  }
  if (status === 'saved') return 'saved'
  if (status === 'seen') return 'seen'
  return 'new'
}

export function learningStatusToGrammarFamiliarity(
  status: LearningStatus | null | undefined
): GrammarFamiliarityStatus {
  if (status === 'learned' || status === 'completed') return 'learned'
  if (status === 'studying') return 'studying'
  if (status === 'seen' || status === 'saved') return 'seen'
  return 'new'
}

export function contentFitFromScore(score: number): ContentFit {
  if (score >= 0.82) return 'review'
  if (score >= 0.45) return 'good-fit'
  if (score >= 0.2) return 'stretch'
  return 'new'
}

function weightedScore(
  parts: Array<{ score: number; weight: number; available: boolean }>
) {
  const active = parts.filter((part) => part.available)
  const totalWeight = active.reduce((sum, part) => sum + part.weight, 0)
  if (totalWeight <= 0) return 0
  return active.reduce(
    (sum, part) => sum + part.score * (part.weight / totalWeight),
    0
  )
}

export function buildContentFamiliaritySummary({
  contentType,
  contentId,
  wordIds,
  wordStatuses,
  characterTexts = [],
  characterStatuses,
  grammarConceptIds,
  grammarStatuses,
  progressRatio = null
}: {
  contentType: ContentFamiliaritySummary['contentType']
  contentId: string
  wordIds: number[]
  wordStatuses?: Map<number, WordFamiliarityStatus>
  characterTexts?: string[]
  characterStatuses?: Map<string, CharacterFamiliarityStatus>
  grammarConceptIds: string[]
  grammarStatuses?: Map<string, GrammarFamiliarityStatus>
  progressRatio?: number | null
}): ContentFamiliaritySummary {
  const uniqueWordIds = uniqueNumbers(wordIds)
  const uniqueCharacters = uniqueStrings(characterTexts)
  const uniqueGrammarIds = uniqueStrings(grammarConceptIds)
  const wordStatusMap = wordStatuses ?? new Map()
  const characterStatusMap = characterStatuses ?? new Map()
  const grammarStatusMap = grammarStatuses ?? new Map()

  const wordStatusesList = uniqueWordIds.map(
    (id) => wordStatusMap.get(id) ?? 'new'
  )
  const characterStatusesList = uniqueCharacters.map(
    (character) => characterStatusMap.get(character) ?? 'new'
  )
  const grammarStatusesList = uniqueGrammarIds.map(
    (id) => grammarStatusMap.get(id) ?? 'new'
  )

  const seenWordCount = wordStatusesList.filter(
    (status) => status !== 'new'
  ).length
  const savedWordCount = wordStatusesList.filter(
    (status) =>
      status === 'saved' || status === 'learning' || status === 'reviewed'
  ).length
  const studiedWordCount = wordStatusesList.filter(
    (status) => status === 'learning' || status === 'reviewed'
  ).length
  const reviewedWordCount = wordStatusesList.filter(
    (status) => status === 'reviewed'
  ).length
  const seenCharacterCount = characterStatusesList.filter(
    (status) => status !== 'new'
  ).length
  const seenGrammarCount = grammarStatusesList.filter(
    (status) => status !== 'new'
  ).length
  const studiedGrammarCount = grammarStatusesList.filter(
    (status) => status === 'studying' || status === 'learned'
  ).length
  const learnedGrammarCount = grammarStatusesList.filter(
    (status) => status === 'learned'
  ).length

  const totalWordCount = uniqueWordIds.length
  const totalCharacterCount = uniqueCharacters.length
  const totalGrammarCount = uniqueGrammarIds.length
  const wordScore = ratio(
    reviewedWordCount +
      (studiedWordCount - reviewedWordCount) * 0.8 +
      (savedWordCount - studiedWordCount) * 0.45 +
      (seenWordCount - savedWordCount) * 0.18,
    totalWordCount
  )
  const characterScore = ratio(seenCharacterCount, totalCharacterCount)
  const grammarScore = ratio(
    learnedGrammarCount +
      (studiedGrammarCount - learnedGrammarCount) * 0.75 +
      (seenGrammarCount - studiedGrammarCount) * 0.4,
    totalGrammarCount
  )
  const readinessScore = weightedScore([
    { score: wordScore, weight: 0.72, available: totalWordCount > 0 },
    {
      score: grammarScore,
      weight: 0.2,
      available: totalGrammarCount > 0
    },
    {
      score: characterScore,
      weight: 0.08,
      available: totalCharacterCount > 0
    }
  ])

  return {
    contentType,
    contentId,
    totalWordCount,
    seenWordCount,
    savedWordCount,
    studiedWordCount,
    reviewedWordCount,
    newWordCount: totalWordCount - seenWordCount,
    seenWordRatio: ratio(seenWordCount, totalWordCount),
    savedWordRatio: ratio(savedWordCount, totalWordCount),
    studiedWordRatio: ratio(studiedWordCount, totalWordCount),
    reviewedWordRatio: ratio(reviewedWordCount, totalWordCount),
    totalCharacterCount,
    seenCharacterCount,
    newCharacterCount: totalCharacterCount - seenCharacterCount,
    characterCoverageRatio: ratio(seenCharacterCount, totalCharacterCount),
    totalGrammarCount,
    seenGrammarCount,
    studiedGrammarCount,
    learnedGrammarCount,
    newGrammarCount: totalGrammarCount - seenGrammarCount,
    grammarCoverageRatio: ratio(seenGrammarCount, totalGrammarCount),
    progressRatio:
      typeof progressRatio === 'number' && Number.isFinite(progressRatio)
        ? Math.min(1, Math.max(0, progressRatio))
        : null,
    familiarityScore: readinessScore,
    readinessScore,
    fit: contentFitFromScore(readinessScore)
  }
}

export function fitLabel(fit: ContentFit) {
  switch (fit) {
    case 'review':
      return 'Mostly review'
    case 'good-fit':
      return 'Good fit'
    case 'stretch':
      return 'Stretch'
    case 'new':
      return 'New material'
  }
}
